import { DATE_PIPE_DEFAULT_OPTIONS, registerLocaleData } from '@angular/common'
import {
    HTTP_INTERCEPTORS,
    provideHttpClient,
    withInterceptors,
    withInterceptorsFromDi,
    withJsonpSupport,
} from '@angular/common/http'
import {
    ApplicationConfig,
    DEFAULT_CURRENCY_CODE,
    LOCALE_ID,
    importProvidersFrom,
    inject,
    provideAppInitializer,
} from '@angular/core'
import {
    TitleStrategy,
    provideRouter,
    withComponentInputBinding,
    withInMemoryScrolling,
    withRouterConfig,
    withViewTransitions,
} from '@angular/router'
import { NgbDateAdapter, NgbDateParserFormatter, NgbTimeAdapter } from '@ng-bootstrap/ng-bootstrap'
import { NgxMaskConfig, provideEnvironmentNgxMask, provideNgxMask } from 'ngx-mask'
import { SurefirePageTitleStrategy } from './app-title-strategy'
import { AppStore } from './core/app.store'
import { CustomDateAdapter } from './shared/custom-date-adaptor'
import { CustomDateParserFormatter } from './shared/custom-date-parser-formatter'
import { CustomTimeAdapter } from './shared/custom-time-adaptor'
import localeUk from '@angular/common/locales/en-GB'
import { BrowserAnimationsModule, provideAnimations } from '@angular/platform-browser/animations'
import { ErrorCatchingInterceptor } from './core/interceptors/error-catching.interceptor'
import { loadingInterceptorFn } from './core/interceptors/loading.interceptor'
import { NgxPermissionsModule } from 'ngx-permissions'
import { JoyrideModule } from 'ngx-joyride'
import { FullCalendarModule } from '@fullcalendar/angular'
import { provideToastr, ToastrModule } from 'ngx-toastr'
import { ROUTES } from './app.routes'
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome'
import { SystemService } from './core/services/system.service'
import { catchError, of, tap } from 'rxjs'
import { SidebarModule } from './shared/components/sidebar/sidebar.module'
import { authHttpInterceptorFn, provideAuth0 } from '@auth0/auth0-angular'
import { environment } from 'src/environments/environment'
import { LoadingRequestCounterService } from './core/interceptors/loading-request-counter.service'
import { sourceInterceptorFn } from './core/interceptors/source.interceptor'

registerLocaleData(localeUk, 'en-GB')

export function initializeApp() {
    const systemService = inject(SystemService)
    const appStore = inject(AppStore)
    return () => {
        return systemService.getSystemInformation().pipe(
            tap((system) => {
                appStore.setSystem(system)
            }),
            catchError(() => {
                return of(null)
            })
        )
    }
}

const maskConfigFunction: () => Partial<NgxMaskConfig> = () => {
    return {
        validation: false,
    }
}

export const appConfig: ApplicationConfig = {
    providers: [
        provideHttpClient(
            withInterceptors([authHttpInterceptorFn, loadingInterceptorFn, sourceInterceptorFn]),
            withInterceptorsFromDi(),
            withJsonpSupport()
        ),
        provideAuth0({
            domain: environment.auth.domain,
            clientId: environment.auth.clientId,
            authorizationParams: {
                audience: environment.auth.authorizationParams?.audience,
                redirect_uri: `${window.location.origin}/callback`,
            },
            httpInterceptor: {
                allowedList: [
                    {
                        // Allow anonymous access to exact or matching endpoints that end with these paths
                        uriMatcher(uri) {
                            const anonymousPaths = ['/system', 'payments/stripe/success']
                            return anonymousPaths.some((path) => uri.endsWith(path))
                        },
                        allowAnonymous: true,
                    },
                    {
                        // Allow anonymous access to anything under /registration/
                        uri: `${environment.baseUrl}registration/*`,
                        allowAnonymous: true,
                    },
                    {
                        // Allow anonymous access to anything under /postcode/
                        uri: `${environment.baseUrl}postcode/*`,
                        allowAnonymous: true,
                    },
                    {
                        // Allow anonymous access to anything under /public/
                        uri: `${environment.baseUrl}public/*`,
                        allowAnonymous: true,
                    },
                    // Protect all other API calls under your API base
                    `${environment.baseUrl}*`,
                ],
            },
            errorPath: '/access-denied',
            // skipRedirectCallback: true,
            // useRefreshTokens: true,
        }),
        provideAppInitializer(() => {
            const initializerFn = initializeApp()
            return initializerFn()
        }),
        provideAnimations(),
        importProvidersFrom(
            NgxPermissionsModule.forRoot(),
            JoyrideModule.forRoot(),
            FullCalendarModule,
            FontAwesomeModule,
            SidebarModule
        ),
        provideToastr(),
        provideEnvironmentNgxMask(maskConfigFunction),
        provideRouter(
            ROUTES,
            withComponentInputBinding(),
            withViewTransitions(),
            withRouterConfig({ paramsInheritanceStrategy: 'always' }),
            withInMemoryScrolling({ scrollPositionRestoration: 'top', anchorScrolling: 'enabled' })
        ),
        {
            provide: TitleStrategy,
            useClass: SurefirePageTitleStrategy,
        },
        {
            provide: LOCALE_ID,
            useValue: 'en-GB',
        },
        {
            provide: DEFAULT_CURRENCY_CODE,
            useValue: 'GBP',
        },
        {
            provide: DATE_PIPE_DEFAULT_OPTIONS,
            useValue: { dateformat: 'shortDate' },
        },
        { provide: NgbDateAdapter, useClass: CustomDateAdapter },
        { provide: NgbDateParserFormatter, useClass: CustomDateParserFormatter },
        { provide: NgbTimeAdapter, useClass: CustomTimeAdapter },
        {
            provide: NgbTimeAdapter,
            useClass: CustomTimeAdapter,
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: ErrorCatchingInterceptor,
            multi: true,
        },
    ],
}
